<script lang="ts" setup>
import type { DialogRootEmits, DialogRootProps } from 'reka-ui'
import { useI18n } from 'vue-i18n'
import { computed, ref, useSlots } from 'vue'
import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DialogTrigger, useForwardPropsEmits, VisuallyHidden } from 'reka-ui'
import { faXmark } from '@fortawesome/duotone-regular-svg-icons'
import ScrollBox from '~/components/utils/ScrollBox.vue'
import Button from '~/components/ui/Button.vue'

type Size = 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

const props = withDefaults(defineProps<DialogRootProps & {
  title: string
  description?: string
  size?: Size
  dismissible?: boolean
}>(), {
  size: 'md',
  dismissible: true
})

const emits = defineEmits<DialogRootEmits>()
const slots = useSlots()

const { t } = useI18n()

const isShaking = ref(false)

const triggerShake = () => {
  if (props.dismissible)
    return
  isShaking.value = true
  setTimeout(() => {
    isShaking.value = false
  }, 500)
}

const hasDescription = computed(() => !!props.description || !!slots.description)

const sizeClass = {
  sm: 'w-full max-w-md',
  md: 'w-full max-w-lg',
  lg: 'w-full max-w-xl',
  xl: 'w-full max-w-2xl',
  xxl: 'w-full max-w-4xl',
} as const satisfies Record<Size, string>

const forwarded = useForwardPropsEmits(props, emits)
</script>

<template>
  <DialogRoot v-slot="{ close }" v-bind="forwarded">
    <DialogTrigger as-child>
      <slot name="trigger" />
    </DialogTrigger>
    <DialogPortal>
      <Transition name="overlay">
        <DialogOverlay class="fixed inset-0 z-50 bg-black/70" />
      </Transition>
      <Transition name="bounce">
        <DialogContent
          :class="[sizeClass[props.size], { 'shake-animation': isShaking }]"
          class="fixed top-1/2 left-1/2 z-50 mb-[100vh] grid max-h-[90svh] -translate-x-1/2 -translate-y-1/2 grid-rows-1 rounded-2xl bg-[#f9f9f9] text-green-50 shadow-2xl"
          @interact-outside="(e) => {
            if (!dismissible) {
              e.preventDefault()
              triggerShake()
            }
          }"
          @escape-key-down="(e) => {
            if (!dismissible) {
              e.preventDefault()
              triggerShake()
            }
          }"
        >
          <ScrollBox class="p-6">
            <DialogTitle class="mb-6 pr-12 text-2xl font-bold">
              {{ title }}
            </DialogTitle>
            <DialogDescription v-if="hasDescription" class="mb-6">
              <slot name="description">
                {{ description }}
              </slot>
            </DialogDescription>
            <VisuallyHidden v-else as-child>
              <DialogDescription>
                {{ t('dialogDescription', { title }) }}
              </DialogDescription>
            </VisuallyHidden>
            <slot name="content" :close />
            <DialogClose v-if="dismissible" as-child>
              <Button
                :aria-label="t('closeDialog')"
                class="hover:not-disabled:bg-green-10 absolute top-6 right-6 bg-white"
                shape="square"
                variant="outline"
                size="lg"
              >
                <font-awesome-icon :icon="faXmark" />
              </Button>
            </DialogClose>
          </ScrollBox>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<style scoped>
.bounce-enter-active {
  animation: bounce-in 0.4s;
}
.bounce-leave-active {
  animation: bounce-out 0.3s reverse;
}
.overlay-enter-active {
  animation: overlay-in 0.3s;
}
.overlay-leave-active {
  animation: overlay-in 0.3s reverse;
}
.shake-animation {
  animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

@keyframes bounce-in {
  0% {
    transform: scale(0) rotate(-20deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1) rotate(0);
    opacity: 1;
  }
}
@keyframes bounce-out {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1) rotate(0);
    opacity: 1;
  }
}
@keyframes overlay-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes shake {
  10%,
  90% {
    transform: translateX(-2px);
  }
  20%,
  80% {
    transform: translateX(4px);
  }
  30%,
  50%,
  70% {
    transform: translateX(-8px);
  }
  40%,
  60% {
    transform: translateX(8px);
  }
}
</style>
